import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Button, ProgressBar, Accordion } from 'react-bootstrap';
import PageHeader from '../../components/PageHeader';
import { apiUrl } from '../../config/apiUrls';
import { UserRound, ArrowLeft } from 'lucide-react';
import { BotMessageSquare, Trophy, Target, MessageCircle, Lightbulb, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

const CommunicationHistory = () => {
    const BASE_URL = process.env.REACT_APP_AI_API_URL;
    const AWS = process.env.REACT_APP_AWS_HOST;
    const studentId = sessionStorage.getItem("id");
    const studentProfileImg = sessionStorage.getItem("profile_image");
    const router = useNavigate()

    const [communications, setCommunications] = useState([]);
    const [selectedCommunication, setSelectedCommunication] = useState(null);
    const [communicationDetails, setCommunicationDetails] = useState(null);

    const [loadingList, setLoadingList] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Analytics View States (toggle between chat and analytics)
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);

    // Mobile View Toggle
    const [isMobileListView, setIsMobileListView] = useState(true);

    // Handle Get Analytics button click - toggle to analytics view
    const handleGetAnalytics = async () => {
        if (!communicationDetails?.communicationdata) return;

        const { evaluation, evaluationMessage, uniqueId } = communicationDetails.communicationdata;

        setShowAnalytics(true);

        if (evaluation === "Evaluated" && evaluationMessage) {
            // Parse and show existing evaluation
            try {
                const parsedData = typeof evaluationMessage === 'string'
                    ? JSON.parse(evaluationMessage)
                    : evaluationMessage;
                setAnalyticsData(parsedData);
            } catch (error) {
                console.error('Error parsing evaluation message:', error);
                setAnalyticsData({ error: 'Failed to parse evaluation data' });
            }
        } else {
            // Call API to get evaluation
            setAnalyticsLoading(true);
            try {
                const response = await fetch(`${BASE_URL}${apiUrl.CommunicationEvaluation}`, {
                    method: "POST",
                    body: JSON.stringify({
                        commId: uniqueId
                    })
                });

                const result = await response.json();

                if (result.success && result.evaluation) {
                    const parsedData = typeof result.evaluation === 'string'
                        ? JSON.parse(result.evaluation)
                        : result.evaluation;
                    setAnalyticsData(parsedData);

                    // Update local state with new evaluation
                    setCommunicationDetails(prev => ({
                        ...prev,
                        communicationdata: {
                            ...prev.communicationdata,
                            evaluation: "Evaluated",
                            evaluationMessage: result.evaluation
                        }
                    }));
                } else {
                    setAnalyticsData({ error: 'Failed to get evaluation' });
                }
            } catch (error) {
                console.error('Error fetching evaluation:', error);
                setAnalyticsData({ error: 'Failed to fetch evaluation. Please try again.' });
            } finally {
                setAnalyticsLoading(false);
            }
        }
    };

    // Handle back button - toggle back to chat view
    const handleBackToChat = () => {
        setShowAnalytics(false);
    };

    // Get color based on score
    const getScoreColor = (score) => {
        if (score >= 8) return 'success';
        if (score >= 6) return 'info';
        if (score >= 4) return 'warning';
        return 'danger';
    };

    // Fetch communication list
    useEffect(() => {
        const fetchCommunicationList = async () => {
            try {
                setLoadingList(true);
                const response = await fetch(`${BASE_URL}${apiUrl.CommunicationList}`, {
                    body: JSON.stringify({
                        userId: studentId
                    }),
                    method: "POST",
                });
                const data = await response.json();

                if (data.communicationdata) {
                    setCommunications(data.communicationdata);
                    // Auto-select first communication ONLY on desktop
                    if (data.communicationdata.length > 0 && window.innerWidth >= 768) {
                        handleSelectCommunication(data.communicationdata[0].uniqueId);
                    }
                }
            } catch (error) {
                console.error('Error fetching communication list:', error);
            } finally {
                setLoadingList(false);
            }
        };

        fetchCommunicationList();
    }, [BASE_URL, studentId]);

    // Fetch communication details
    const handleSelectCommunication = async (uniqueId) => {
        setSelectedCommunication(uniqueId);
        setLoadingDetails(true);
        setShowAnalytics(false);
        setIsMobileListView(false);

        try {
            const response = await fetch(`${BASE_URL}${apiUrl.CommunicationHistory}`, {
                body: JSON.stringify({
                    uniqueId: uniqueId
                }),
                method: "POST",
            });
            const data = await response.json();

            if (data) {
                // Sort messages by timestamp
                const sortedHistory = data.history?.sort((a, b) =>
                    new Date(a.timestamp) - new Date(b.timestamp)
                ) || [];

                setCommunicationDetails({
                    ...data,
                    history: sortedHistory
                });
            }
        } catch (error) {
            console.error('Error fetching communication details:', error);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Format timestamp
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Get difficulty badge color
    const getDifficultyBadge = (level) => {
        const colors = {
            easy: 'success',
            medium: 'warning',
            hard: 'danger'
        };
        return colors[level] || 'secondary';
    };

    return (
        <>
            <Row>
                <Col>
                    <PageHeader
                        title="Communication History"
                        breadcrumbs={[
                            { label: "Dashboard", path: "/dashboard" },
                            { label: "Practice With AI", path: '/practice-with-ai' },
                            { label: "Communication History", active: true },
                        ]}
                    />
                </Col>
            </Row>

            <div className="">
                <Row className="g-0" style={{ height: 'calc(100vh - 100px)', minHeight: '500px' }}>
                    {/* Left Panel - Communication List */}
                    <Col md={4} lg={3} className={`h-100 ${isMobileListView ? 'd-block' : 'd-none d-md-block'}`}>
                        <Card className="h-100 border-end rounded-0 rounded-start" style={{ borderRadius: '12px 0 0 12px' }}>
                            <Card.Header className="bg-white border-bottom py-3 px-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <h6 className="mb-0 fw-bold">
                                        <i className="bi bi-chat-left-text-fill text-primary me-2"></i>
                                        Sessions
                                    </h6>
                                    <Badge bg="primary" pill>{communications.length}</Badge>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0 overflow-auto" style={{ maxHeight: 'calc(100% - 60px)' }}>
                                {loadingList ? (
                                    <div className="d-flex justify-content-center align-items-center py-5">
                                        <Spinner animation="border" variant="primary" size="sm" />
                                    </div>
                                ) : communications.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="bi bi-chat-square-text fs-1 d-block mb-3 opacity-50"></i>
                                        <p className="mb-0">No sessions found</p>
                                        <small>Start a new session to see it here</small>
                                        <div className='d-flex justify-content-center'>
                                            <Button
                                                variant="primary"
                                                onClick={() => router('/practice-with-ai/communication-skills')}
                                                className="mt-3"
                                            >
                                                Start New Session
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {communications.map((comm) => (
                                            <div
                                                key={comm.uniqueId}
                                                onClick={() => handleSelectCommunication(comm.uniqueId)}
                                                className={`p-3 border-bottom cursor-pointer debate-list-item ${selectedCommunication === comm.uniqueId ? 'bg-primary bg-opacity-10 border-start border-4 border-primary' : ''
                                                    }`
                                                }
                                                style={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    borderLeft: selectedCommunication === comm.uniqueId ? '4px solid #0d6efd' : '4px solid transparent'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (selectedCommunication !== comm.uniqueId) {
                                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (selectedCommunication !== comm.uniqueId) {
                                                        e.currentTarget.style.backgroundColor = '';
                                                    }
                                                }}
                                            >
                                                <div className="d-flex align-items-start">
                                                    <div className="flex-grow-1 min-width-0">
                                                        <h6 className={`mb-1 fw-semibold text-truncate ${selectedCommunication === comm.uniqueId ? 'text-white' : ''}`} style={{ fontSize: '0.9rem' }}>
                                                            {comm.prepType?.replace(/[_-]/g, " ") || "Communication Session"}
                                                        </h6>
                                                        <small className={`d-block text-truncate ${selectedCommunication === comm.uniqueId ? 'text-white' : ''}`}>
                                                            Click to view details
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className='d-flex justify-content-center'>
                                            <Button
                                                variant="primary"
                                                onClick={() => router('/practice-with-ai/communication-skills')}
                                                className="mt-3"
                                            >
                                                Start New Session
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Right Panel - Communication Details & Chat */}
                    <Col md={8} lg={9} className={`h-100 ${!isMobileListView ? 'd-block' : 'd-none d-md-block'}`}>
                        <Card className="h-100 rounded-0 rounded-end" style={{ borderRadius: '0 12px 12px 0' }}>
                            {!selectedCommunication ? (
                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                                    <i className="bi bi-chat-square-dots fs-1 mb-3 opacity-50"></i>
                                    <h5 className="fw-semibold">Select a session</h5>
                                    <p className="mb-0">Choose a session from the list to view the conversation</p>
                                </div>
                            ) : loadingDetails ? (
                                <div className="d-flex justify-content-center align-items-center h-100">
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : communicationDetails ? (
                                <>
                                    {/* Communication Header */}
                                    <Card.Header className="bg-white border-bottom py-3 px-4">
                                        <div className="d-flex align-items-start justify-content-between">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                    {showAnalytics && (
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            onClick={handleBackToChat}
                                                            className="d-flex align-items-center gap-1"
                                                        >
                                                            <ArrowLeft size={16} />
                                                            Back to Chat
                                                        </Button>
                                                    )}
                                                    {!showAnalytics && (
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => setIsMobileListView(true)}
                                                            className="d-md-none d-flex align-items-center gap-1"
                                                        >
                                                            <ArrowLeft size={16} />
                                                            Sessions
                                                        </Button>
                                                    )}
                                                    <h5 className="mb-0 fw-bold text-capitalize">
                                                        {showAnalytics ? (
                                                            <span className="d-flex align-items-center gap-2">
                                                                <Trophy size={20} className="text-primary" />
                                                                Session Analytics
                                                            </span>
                                                        ) : (
                                                            communicationDetails.communicationdata?.topic?.replace(/[_-]/g, " ")
                                                        )}
                                                    </h5>
                                                </div>
                                                <div className="d-flex flex-wrap justify-content-between gap-2 align-items-center">
                                                    <div className='d-flex flex-wrap gap-2 align-items-center'>
                                                        <Badge>{formatDate(communicationDetails.communicationdata?.createdAt)}</Badge>
                                                        {/* Level might not be in communication data, checking if it exists */}
                                                        {communicationDetails.communicationdata?.level && (
                                                            <Badge bg={getDifficultyBadge(communicationDetails.communicationdata?.level)} className="text-capitalize">
                                                                Level: {communicationDetails.communicationdata?.level}
                                                            </Badge>
                                                        )}
                                                        <Badge bg="info" className="text-capitalize">
                                                            Type: Communication
                                                        </Badge>
                                                        {communicationDetails.communicationdata?.duration && (
                                                            <Badge bg="secondary">
                                                                <i className="bi bi-clock me-1"></i>
                                                                {communicationDetails.communicationdata?.duration || 0} min
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {!showAnalytics && (
                                                <div>
                                                    {(communicationDetails?.history?.length > 0 || !showAnalytics) && <Button variant="primary" onClick={handleGetAnalytics}>Get Analytics</Button>}
                                                </div>
                                            )}
                                        </div>
                                    </Card.Header>

                                    {/* Conditional Content - Chat or Analytics */}
                                    <Card.Body
                                        className="overflow-auto"
                                        style={{
                                            maxHeight: 'calc(100% - 10px)',
                                            background: showAnalytics ? '#fff' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                            padding: isMobile ? '0.5rem' : '1.5rem'
                                        }}
                                    >
                                        {showAnalytics ? (
                                            /* Analytics View */
                                            analyticsLoading ? (
                                                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                                    <Spinner animation="border" variant="primary" className="mb-3" />
                                                    <p className="text-muted">Analyzing your performance...</p>
                                                </div>
                                            ) : analyticsData?.error ? (
                                                <div className="text-center py-5">
                                                    <i className="bi bi-exclamation-triangle text-warning fs-1 mb-3 d-block"></i>
                                                    <p className="text-muted">{analyticsData.error}</p>
                                                </div>
                                            ) : analyticsData ? (
                                                <div className="analytics-content">
                                                    {/* Overall Score */}
                                                    <div className="text-center mb-4 p-4 bg-light rounded-3">
                                                        <h2 className="display-4 fw-bold text-primary mb-2">{analyticsData.score}/10</h2>
                                                        <p className="text-muted mb-0">Overall Performance Score</p>
                                                    </div>

                                                    {/* Metrics Section */}
                                                    {analyticsData.metrics && (
                                                        <div className="mb-4">
                                                            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                                                <Target size={20} className="text-primary" />
                                                                Performance Metrics
                                                            </h5>
                                                            <div className="row g-3">
                                                                {Object.entries(analyticsData.metrics).map(([key, value]) => (
                                                                    <div className="col-md-6" key={key}>
                                                                        <div className="p-3 border rounded-3">
                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                <span className="text-capitalize fw-medium">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                                                                <Badge bg={getScoreColor(value)}>{value}/10</Badge>
                                                                            </div>
                                                                            <ProgressBar
                                                                                now={value * 10}
                                                                                variant={getScoreColor(value)}
                                                                                style={{ height: '8px' }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Detailed Feedback Section */}
                                                    {analyticsData.feedback && (
                                                        <div className="mb-4">
                                                            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                                                <MessageCircle size={20} className="text-primary" />
                                                                Detailed Feedback
                                                            </h5>
                                                            <Accordion defaultActiveKey="0">
                                                                {Object?.entries(analyticsData?.feedback)?.map(([key, value], index) => (
                                                                    <Accordion.Item eventKey={String(index)} key={key} className="mb-2 p-2 border rounded">
                                                                        <Accordion.Header className="bg-white">
                                                                            <span className="text-capitalize fw-medium">
                                                                                {key.replace(/([A-Z])/g, ' $1').trim()}
                                                                            </span>
                                                                        </Accordion.Header>
                                                                        <Accordion.Body className="bg-white">
                                                                            <p className="mb-0 text-muted">{value}</p>
                                                                        </Accordion.Body>
                                                                    </Accordion.Item>
                                                                ))}
                                                            </Accordion>
                                                        </div>
                                                    )}

                                                    {/* Areas to Focus */}
                                                    {analyticsData.areasToFocus && analyticsData.areasToFocus.length > 0 && (
                                                        <div className="mb-4">
                                                            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                                                <Lightbulb size={20} className="text-warning" />
                                                                Areas to Focus
                                                            </h5>
                                                            <ul className="list-group">
                                                                {analyticsData.areasToFocus.map((area, index) => (
                                                                    <li
                                                                        key={index}
                                                                        className="list-group-item d-flex align-items-center gap-2 border-start border-warning"
                                                                    >
                                                                        <span className="badge bg-warning text-dark">{index + 1}</span>
                                                                        {area}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {/* Detailed Feedback Summary */}
                                                    {analyticsData.detailedFeedback && (
                                                        <div className="mb-0">
                                                            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                                                <TrendingUp size={20} className="text-success" />
                                                                Summary
                                                            </h5>
                                                            <div className="p-3 bg-success bg-opacity-10 border border-success rounded-3">
                                                                <p className="mb-0" style={{ lineHeight: '1.7' }}>{analyticsData.detailedFeedback}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : null
                                        ) : (
                                            /* Chat Messages View */
                                            <div className="d-flex flex-column h-100 " style={{ gap: isMobile ? '0.3rem' : '1rem' }}>
                                                {communicationDetails?.history?.length === 0 ? (
                                                    <div className="d-flex justify-content-center align-items-center h-100 text-muted text-center">
                                                        <div>
                                                            <h5><strong>No conversation history available.</strong></h5>
                                                            This completed session does not contain any saved messages.
                                                        </div>
                                                    </div>
                                                ) : <>
                                                    {communicationDetails?.history?.map((message, index) => (
                                                        <div
                                                            key={index}
                                                            className={`d-flex ${message.sender === 'USER' ? 'justify-content-end' : 'justify-content-start'}`}
                                                        >
                                                            <div
                                                                className={`d-flex align-items-end gap-2 mb-2 ${message.sender === 'USER' ? 'flex-row-reverse' : ''}`}
                                                                style={{ maxWidth: isMobile ? '90%' : '70%' }}
                                                            >
                                                                {/* Avatar */}
                                                                <div
                                                                    className={`flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center border border-2 p-1 border-dark`}
                                                                    style={{ width: isMobile ? '25px' : '32px', height: isMobile ? '25px' : '32px' }}
                                                                >
                                                                    {message.sender === 'USER' ? <img
                                                                        src={`${AWS}${studentProfileImg}`}
                                                                        alt="user-img"
                                                                        className="rounded-circle"
                                                                        onError={(e) => {
                                                                            e.target.src = "/assets/images/avtar.png";
                                                                        }}
                                                                        style={{
                                                                            width: isMobile ? 30 : 40,
                                                                            height: isMobile ? 30 : 40,
                                                                            objectFit: "cover",
                                                                            border: "3px solid #fff",
                                                                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                                            zIndex: 3,
                                                                        }}
                                                                    /> : <BotMessageSquare />}
                                                                </div>

                                                                {/* Message Bubble */}
                                                                <div
                                                                    className={`p-3 ${message.sender === 'USER'
                                                                        ? 'bg-primary text-white'
                                                                        : 'bg-white border'
                                                                        }`}
                                                                    style={{
                                                                        borderRadius: message.sender === 'USER'
                                                                            ? '18px 18px 4px 18px'
                                                                            : '18px 18px 18px 4px',
                                                                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                                                                    }}
                                                                >
                                                                    <p className="mb-1" style={{ fontSize: isMobile ? '0.8rem' : '0.95rem', lineHeight: '1.5' }}>
                                                                        {message.content}
                                                                    </p>
                                                                    <small
                                                                        className={`d-block text-end ${message.sender === 'USER' ? 'text-white-50' : 'text-muted'
                                                                            }`}
                                                                        style={{ fontSize: '0.7rem' }}
                                                                    >
                                                                        {formatTime(message.timestamp)}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </>}
                                            </div>
                                        )}
                                    </Card.Body>
                                </>
                            ) : (
                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                                    <i className="bi bi-exclamation-circle fs-1 mb-3 opacity-50"></i>
                                    <h5 className="fw-semibold">Error loading session</h5>
                                    <p className="mb-0">Could not load the session details. Please try again.</p>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>


            <style>{`
        .min-width-0 {
          min-width: 0;
        }
        
        .debate-list-item:hover {
          background-color: #f8f9fa;
        }
        
        .debate-list-item.selected {
          background-color: rgba(13, 110, 253, 0.1);
          border-left: 4px solid #0d6efd;
        }

        /* Custom scrollbar */
        .overflow-auto::-webkit-scrollbar {
          width: 6px;
        }
        
        .overflow-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        .overflow-auto::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        
        .overflow-auto::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
        </>
    );
};

export default CommunicationHistory;
